import { useQuery, useQueries, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useEventSource, type ConnectionStatus, type SSEEvent } from "@/hooks/useEventSource";
import {
  mailboxApi,
  type Job,
  type JobAttachment,
  type EnqueueJobInput,
  type PostTurnInput,
  type ActiveJobQuery,
} from "@/lib/api/mailbox";
import { TERMINAL_JOB_STATUSES } from "@mycompbox/shared";
import {
  ACTIVE_JOB_POLL_MS,
  LIVE_JOB_POLL_MS,
  MACHINE_LIST_POLL_MS,
  STREAMED_JOB_FALLBACK_POLL_MS,
} from "@/lib/polling";
import { epicRequestKeys } from "./use-epic-requests";

/**
 * Input to {@link useUploadAttachment}: either a picked/pasted `File` (multipart
 * upload) or an image `url` to ingest server-side. One hook covers both entry
 * points so the tray can treat every attachment source uniformly.
 */
export type UploadAttachmentInput = { file: File } | { url: string };

/**
 * Mailbox query/mutation hooks (ENG-123 browser flow).
 *
 * - `useMachines` keeps the online/offline state fresh (light polling).
 * - `useJob` live-polls a job every ~2s while it's active and stops once the
 *   job reaches a terminal state (done/failed) — used by both the interview UI
 *   and the execution-progress view.
 */

// =============================================================================
// Query Keys
// =============================================================================

export const mailboxKeys = {
  all: ["mailbox"] as const,
  machines: () => [...mailboxKeys.all, "machines"] as const,
  jobs: () => [...mailboxKeys.all, "jobs"] as const,
  job: (id: string) => [...mailboxKeys.jobs(), id] as const,
  active: (query: ActiveJobQuery) => [...mailboxKeys.jobs(), "active", query] as const,
};

/** Job states after which there is nothing left to poll (shared source of truth). */
const TERMINAL_STATUSES = new Set<Job["status"]>(TERMINAL_JOB_STATUSES);

// Cadences live in src/lib/polling.ts, which documents why each is set where it
// is (Cloud Run bills vCPU for request wall-clock, so poll frequency is a cost
// decision). The live-job poll stays deliberately fast — it self-terminates on a
// terminal status, so it is bounded by real work rather than by tab lifetime.
// Job state only — progress numbers come from useRunProgress (ENG-370).
const JOB_POLL_INTERVAL_MS = LIVE_JOB_POLL_MS;
/** While a conversation waits on the person (e.g. another tab or device replies). */
const WAITING_ON_PERSON_POLL_MS = 30_000;
const MACHINE_POLL_INTERVAL_MS = MACHINE_LIST_POLL_MS;

// =============================================================================
// Query Hooks
// =============================================================================

/** Lists the current user's paired machines, refreshed periodically. */
export function useMachines() {
  return useQuery({
    queryKey: mailboxKeys.machines(),
    queryFn: mailboxApi.listMachines,
    refetchInterval: MACHINE_POLL_INTERVAL_MS,
  });
}

/**
 * Fetches a job and live-polls it while active. Polling stops automatically
 * once the job is done/failed. Pass `{ poll: false }` to disable polling
 * (e.g. for a static/historical view). Pass `streamStatus` (from
 * {@link useJobStream}) to poll slower while the job's event stream is
 * connected (ENG-389).
 */
export function useJob(
  id: string | undefined,
  options?: { poll?: boolean; streamStatus?: ConnectionStatus | undefined }
) {
  const poll = options?.poll ?? true;
  const streamStatus = options?.streamStatus;
  return useQuery({
    queryKey: mailboxKeys.job(id ?? ""),
    queryFn: () => mailboxApi.getJob(id ?? ""),
    enabled: !!id,
    refetchInterval: (query) => (poll ? jobRefetchInterval(query.state.data, streamStatus) : false),
  });
}

/** Most jobs {@link useJobChain} walks back through (a guard against cycles). */
export const JOB_CHAIN_LIMIT = 25;

/**
 * ENG-364: a job and every job it continues (`continuedFromJobId`), newest
 * first. Each predecessor is fetched once its successor has loaded (the
 * re-render reads it from the cache), so the list grows as the chain is
 * discovered. Only the newest job live-polls; older links are history.
 */
export function useJobChain(id: string | undefined) {
  const queryClient = useQueryClient();
  const ids: string[] = [];
  let next: string | null | undefined = id;
  while (next && ids.length < JOB_CHAIN_LIMIT && !ids.includes(next)) {
    ids.push(next);
    next = queryClient.getQueryData<Job>(mailboxKeys.job(next))?.continuedFromJobId;
  }
  return useQueries({
    queries: ids.map((jobId, i) => ({
      queryKey: mailboxKeys.job(jobId),
      queryFn: () => mailboxApi.getJob(jobId),
      refetchInterval:
        i === 0
          ? (query: { state: { data: Job | undefined } }) => jobRefetchInterval(query.state.data)
          : (false as const),
    })),
  });
}

/**
 * Poll cadence for a job: stop once it is terminal (done/failed/cancelled),
 * slow down while it waits on the person, and otherwise poll live — including
 * while "cancelling", so the page flips to "cancelled" as soon as the runner
 * stops the work.
 *
 * `streamStatus` is the job's SSE connection status (ENG-389). While it is
 * "connected", live events carry the updates and polling drops to the slower
 * {@link STREAMED_JOB_FALLBACK_POLL_MS} safety net; any other status, or none,
 * keeps the fast {@link LIVE_JOB_POLL_MS} cadence.
 */
export function jobRefetchInterval(
  job: Pick<Job, "status"> | undefined,
  streamStatus?: ConnectionStatus
): number | false {
  if (job && TERMINAL_STATUSES.has(job.status)) return false;
  // The person's turn: nothing changes until they write (which refreshes
  // the job itself), so check only now and then.
  // Same for a job parked until the AI usage window reopens (ENG-333): it
  // only moves at its resume time, minutes to hours away.
  if (job?.status === "awaiting_input" || job?.status === "waiting") return WAITING_ON_PERSON_POLL_MS;
  if (streamStatus === "connected") return STREAMED_JOB_FALLBACK_POLL_MS;
  return JOB_POLL_INTERVAL_MS;
}

/**
 * The unfinished plan/build job for an epic or epic request, or null. Polls
 * slowly; it only drives the Cancel button on those pages.
 */
export function useActiveJob(query: ActiveJobQuery, enabled = true) {
  return useQuery({
    queryKey: mailboxKeys.active(query),
    queryFn: () => mailboxApi.getActiveJob(query),
    enabled,
    refetchInterval: ACTIVE_JOB_POLL_MS,
    refetchOnWindowFocus: true,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Cancels a job (ENG-302). On success the job cache is primed with the
 * returned job (usually "cancelling") and invalidated so polling picks up the
 * final "cancelled" state.
 */
export function useCancelJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mailboxApi.cancelJob(id),
    onSuccess: (job) => {
      queryClient.setQueryData(mailboxKeys.job(job.id), (prev: Job | undefined) =>
        // Cancelling adds no turns — keep the ones already loaded.
        prev ? { ...prev, ...job, turns: prev.turns, progress: job.progress ?? prev.progress } : job
      );
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.job(job.id) });
      void queryClient.invalidateQueries({ queryKey: [...mailboxKeys.jobs(), "active"] });
    },
  });
}

/** ER-130: "All merged — continue" (requireMerged) or "Restart" on a build. */
export function useContinueRun(mode: "continue" | "restart") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, deferredFeatureIds }: { jobId: string; deferredFeatureIds?: string[] }) =>
      mode === "continue"
        ? mailboxApi.continueRun(jobId, deferredFeatureIds ? { deferredFeatureIds } : {})
        : mailboxApi.restartRun(jobId, deferredFeatureIds ? { deferredFeatureIds } : {}),
    onSuccess: (_result, { jobId }) => {
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.job(jobId) });
      void queryClient.invalidateQueries({ queryKey: [...mailboxKeys.jobs(), "active"] });
    },
  });
}

/** Enqueues a job for a machine and primes the job cache with the result. */
export function useEnqueueJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EnqueueJobInput) => mailboxApi.enqueueJob(input),
    onSuccess: (job) => {
      queryClient.setQueryData(mailboxKeys.job(job.id), job);
    },
  });
}

/** Posts a user turn (interview answer) and updates the cached job. */
export function usePostTurn(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PostTurnInput) => mailboxApi.postTurn(jobId, input),
    onSuccess: (job) => {
      queryClient.setQueryData(mailboxKeys.job(jobId), job);
    },
  });
}

/**
 * Sends a follow-up to a finished build (ENG-364). On success it primes the
 * cache with the new job and invalidates the parent build plus the job lists,
 * so the idea stream picks up the new link in the continuation chain.
 */
export function useCreateFollowUp(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: PostTurnInput) => mailboxApi.createFollowUp(jobId, input),
    onSuccess: (job) => {
      queryClient.setQueryData(mailboxKeys.job(job.id), job);
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.job(jobId) });
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.job(job.id) });
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.jobs() });
      // The idea page reads its build chain from the idea facts; the job carries
      // no idea id, so refresh every idea detail (only mounted ones refetch).
      void queryClient.invalidateQueries({ queryKey: epicRequestKeys.details() });
    },
  });
}

/**
 * Uploads an image attachment to a job (ENG-152). Accepts either a `File`
 * (multipart) or an image `{ url }` (server-side ingest) and resolves to the
 * stored {@link JobAttachment}. Per-attachment UI state (spinner → check,
 * inline error) is owned by the caller so multiple concurrent uploads can each
 * track their own status; this hook is just the transport.
 */
export function useUploadAttachment(jobId: string) {
  return useMutation<JobAttachment, unknown, UploadAttachmentInput>({
    mutationFn: (input: UploadAttachmentInput) =>
      "file" in input
        ? mailboxApi.uploadAttachment(jobId, input.file)
        : mailboxApi.uploadAttachmentUrl(jobId, input.url),
  });
}

/**
 * Renames a paired machine (ENG-170) and invalidates the machine list so the
 * new name propagates everywhere that reads `useMachines` — the machine-list UI
 * and the "Send to my machine" dropdown share that source.
 */
export function useRenameMachine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      mailboxApi.renameMachine(input.id, input.name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.machines() });
    },
  });
}

/**
 * Toggles a machine's auto-continue setting (ENG-334) and invalidates the
 * machine list so the switch reflects the server's value.
 */
export function useSetMachineAutoContinue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; autoContinue: boolean }) =>
      mailboxApi.setMachineAutoContinue(input.id, input.autoContinue),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.machines() });
    },
  });
}

/**
 * Removes (soft-revokes) a paired machine (ENG-170) and invalidates the machine
 * list. Because the API already excludes revoked machines from GET /machines,
 * the removed row drops out of both the machine-list UI and the launcher
 * dropdown on the next fetch — no client-side filtering needed.
 */
export function useRemoveMachine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mailboxApi.removeMachine(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mailboxKeys.machines() });
    },
  });
}

/** SSE endpoint carrying job.stream / job.turn events for a subscribed job. */
export const JOB_STREAM_EVENTS_URL = "/api/v1/events";

export interface JobStreamResult {
  /** Partial reply text assembled so far for the in-progress turn ("" when none). */
  draft: string;
  /** Connection status of the underlying event stream. */
  status: ConnectionStatus;
}

interface StreamDeltaPayload {
  jobId: string;
  seq: number;
  delta: string;
}

function isStreamDelta(data: unknown): data is StreamDeltaPayload {
  if (typeof data !== "object" || data === null) return false;
  const d = data as Record<string, unknown>;
  return typeof d.jobId === "string" && typeof d.seq === "number" && Number.isFinite(d.seq) && typeof d.delta === "string";
}

function eventJobId(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const id = (data as Record<string, unknown>).jobId;
  return typeof id === "string" ? id : undefined;
}

/**
 * Live draft of a job's in-progress reply (ENG-389). Subscribes to the job's
 * SSE events: `job.stream` deltas are assembled in `seq` order (duplicates are
 * ignored, so out-of-order or re-delivered slices still read correctly), and
 * `job.turn` — the committed turn — clears the draft and refetches the job so
 * the real turn replaces it.
 */
export function useJobStream(jobId: string | undefined, options: { enabled?: boolean } = {}): JobStreamResult {
  const queryClient = useQueryClient();
  const enabled = (options.enabled ?? true) && !!jobId;
  const slicesRef = useRef<Map<number, string>>(new Map());
  const [draft, setDraft] = useState("");

  // A different job starts from an empty draft.
  useEffect(() => {
    slicesRef.current = new Map();
    setDraft("");
  }, [jobId]);

  const onEvent = useCallback(
    (event: SSEEvent) => {
      if (!jobId || eventJobId(event.data) !== jobId) return;
      if (event.type === "job.stream") {
        if (!isStreamDelta(event.data)) return;
        const slices = slicesRef.current;
        if (slices.has(event.data.seq)) return;
        slices.set(event.data.seq, event.data.delta);
        const ordered = [...slices.entries()].sort((a, b) => a[0] - b[0]).map(([, delta]) => delta);
        setDraft(ordered.join(""));
      } else if (event.type === "job.turn") {
        slicesRef.current = new Map();
        setDraft("");
        void queryClient.invalidateQueries({ queryKey: mailboxKeys.job(jobId) });
      }
    },
    [jobId, queryClient],
  );

  const { status } = useEventSource({
    url: JOB_STREAM_EVENTS_URL,
    ...(jobId && { jobId }),
    enabled,
    onEvent,
  });

  return { draft, status };
}
